import { useCallback, useEffect } from 'react';
import { LlmToolRequestActions, fullstorySelectors } from '@fullstory/redux';
import { useSelector } from 'react-redux';
import { RequestConfig, useAiLabelSuggestions } from '@fullstory/library-ui';
import { Type } from '@fullstory/proto/fs/api/vars';
import { ElementPropertyRule } from '@fullstory/proto/fs/services/search-settings';
import { useFlags } from '@fullstory/react-hooks';

interface Source {
  attribute?: ElementPropertyRule.SourceAttribute | null;
  innerText?: ElementPropertyRule.SourceInnerText | null;
  static?: ElementPropertyRule.SourceStatic | null;
}

interface UseAiPropertyLabelSuggestionArgs {
  namedElementId: string;
  propertyType: Type;
  sampleValues: string[];
  propertyRuleDescription: string;
  source: Source;
}

// properties don't have additional context or spec
const requestConfigs: Array<{ choices: string[]; useAdditionalContext: boolean }> = [
  {
    choices: [],
    useAdditionalContext: false,
  },
  {
    choices: [],
    useAdditionalContext: false,
  },
  {
    choices: [],
    useAdditionalContext: false,
  },
];

export const useAiPropertyLabelSuggestions = ({
  namedElementId,
  propertyType,
  sampleValues,
  propertyRuleDescription,
  source,
}: UseAiPropertyLabelSuggestionArgs) => {
  const orgId = useSelector(fullstorySelectors.getOrgId);
  const flags = useFlags();
  const elementPropertyRulesV2Enabled = flags.featureFlags().AppElementPropertyRulesV2;
  const buildRequest = useCallback(
    (reqConfig: RequestConfig, blocklist: string[]) =>
      LlmToolRequestActions.suggestPropertyLabelSingle({
        orgId,
        namedElementId,
        propertyRule: {
          propertyType,
          sampleValues,
          propertyRuleDescription,
          attribute: source.attribute,
          innerText: source.innerText,
          static: source.static,
        },
        choices: reqConfig.choices,
        blocklist,
      }),
    [
      orgId,
      namedElementId,
      propertyType,
      sampleValues,
      propertyRuleDescription,
      source.attribute,
      source.innerText,
      source.static,
    ],
  );

  const getSpecSlug = useCallback(() => null, []);

  const { clearBlockList, ...aiLabelSuggestions } = useAiLabelSuggestions(
    buildRequest,
    getSpecSlug,
    requestConfigs,
  );

  useEffect(() => {
    if (!elementPropertyRulesV2Enabled) {
      return;
    }
    clearBlockList();
  }, [
    elementPropertyRulesV2Enabled,
    propertyType,
    namedElementId,
    propertyRuleDescription,
    source.attribute,
    source.innerText,
    source.static,
    clearBlockList,
  ]);

  return { clearBlockList, ...aiLabelSuggestions };
};
